import { motion } from 'framer-motion';
import { useEffect } from 'react';
import styled from 'styled-components';
import { device } from '../../styles/devices';
import { color, gutter } from '../../styles/mixins';
import { m28 } from '../../styles/typography';
import { getPriceBreakdown } from '../../utils/price-calculations';
import { Button } from '../button/button';
import { FormatAmount } from '../format-amount/format-amount';

// #region Styles
const Wrap = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  @media ${device.tablet} {
    align-items: center;
  }
`;

const Host = styled(motion.div)`
  position: relative;
  z-index: 1;
  max-width: 500px;
  margin: ${gutter(2)} ${gutter(2)};
  background-color: ${color('white')};
  filter: drop-shadow(0px 8px 24px rgba(0, 0, 0, 0.1));
  border-radius: ${(props) => props.theme.border.radius}px;
`;

const Header = styled.div`
  padding: ${gutter(5)} ${gutter(4)} ${gutter(2)};
`;

const Title = styled.div`
  ${m28};
`;

const Content = styled.div`
  padding: 0 ${gutter(4)} ${gutter(4)};
  border-bottom: 1px solid ${color('gray30')};
`;

const TableContainer = styled.div`
  background-color: ${color('blue10')};
  padding: 16px;
  margin: 24px 0 0;
`;

const BreakdownTable = styled.table`
  width: 100%;
  margin-bottom: 8px;
  tbody tr td {
    padding-bottom: 4px;
  }
  tbody tr th {
    font-weight: 500;
    text-align: left;
  }
  tbody tr td:nth-child(even),
  tbody tr th:nth-child(even) {
    text-align: right;
  }
  tbody tr td hr {
    border-top: 1px solid ${color('gray50')};
    margin: 8px 0;
  }
`;

const SmallPrint = styled.p`
  color: ${color('gray80')};
  font-size: 12px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${gutter(4)} ${gutter(4)} ${gutter(2)};
  > * {
    margin-bottom: ${gutter(2)};
  }
`;

const Overlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 0;
`;
// #endregion

interface ConfirmBidModalProps {
  bidAmount: number;
  onClose: () => void;
  disabled?: boolean;
  buyersPremiumPercentage: number;
  overheadPremiumPercentage: number;
}

export const ConfirmBidModal = ({
  bidAmount,
  buyersPremiumPercentage,
  overheadPremiumPercentage,
  onClose,
  disabled,
}: ConfirmBidModalProps) => {
  const titleId = 'cbm-title';
  const contentId = 'cbm-content';

  const closeOnEscapePress = (event: KeyboardEvent) => {
    if (event.code === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', closeOnEscapePress);
    return () => window.removeEventListener('keydown', closeOnEscapePress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priceBreakdown = getPriceBreakdown(
    bidAmount,
    buyersPremiumPercentage,
    overheadPremiumPercentage
  );

  return (
    <Wrap>
      <Host
        initial={{ opacity: 0, translateY: 350 }}
        animate={{ opacity: 1, translateY: 0 }}
        exit={{ opacity: 0, translateY: 350 }}
        role="dialog"
        aria-labelledby={titleId}
        aria-describedby={contentId}
        aria-modal
        tabIndex={-1}
      >
        <Header>
          <Title id={titleId}>Confirm Your Bid</Title>
        </Header>
        <Content>
          <p>You are placing a maximum bid of:</p>
          <TableContainer>
            <BreakdownTable>
              <tbody>
                <tr>
                  <td>Your bid</td>
                  <td>
                    <FormatAmount value={priceBreakdown.hammerPrice} />
                  </td>
                </tr>
                {priceBreakdown.buyersPremium > 0 && (
                  <tr>
                    <td>Buyer&apos;s premium</td>
                    <td>
                      <FormatAmount value={priceBreakdown.buyersPremium} />
                    </td>
                  </tr>
                )}

                {priceBreakdown.overheadPremium > 0 && (
                  <tr>
                    <td>Overhead premium</td>
                    <td>
                      <FormatAmount value={priceBreakdown.overheadPremium} />
                    </td>
                  </tr>
                )}

                <tr>
                  <td colSpan={2}>
                    <hr />
                  </td>
                </tr>
                <tr>
                  <th>Total price</th>
                  <th>
                    <FormatAmount value={priceBreakdown.total} />
                  </th>
                </tr>
              </tbody>
            </BreakdownTable>
            <SmallPrint>
              Total price excludes and applicable tax or shipping
            </SmallPrint>
          </TableContainer>
        </Content>
        <Actions>
          <Button type="submit" disabled={disabled}>
            {`Place your bid | `}
            <FormatAmount value={priceBreakdown.total} />
          </Button>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
        </Actions>
      </Host>
      <Overlay onMouseDown={() => onClose()} />
    </Wrap>
  );
};
